import { Modal } from '../../../components/modal/modal';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Button } from '../../../components/form/button';
import { v4 as uuidv4 } from 'uuid';
import {
  CanInventoryReturnValidationError,
  InventoryReturnRequest,
  OffRentRequest,
  Order,
  OrderProduct,
  OrderReturnStatus,
  OrderType,
} from '../../../clients/quipli-service/order/order';
import { Location } from '../../../clients/quipli-service/location/location';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { Spinner } from '../../../components/spinner/spinner';
import { toast } from '../../../util/toasts';
import { Toggle } from '../../../components/form/toggle';
import { Invoice } from '../../../clients/quipli-service/invoice/invoice';
import { PaymentReason, PaymentStatus } from '../../../clients/quipli-service/payment/payment';
import { debounce, Dictionary } from 'lodash';
import './status-transitions.scss';
import { ValidationError } from '../../../components/validation-error/validation-error';
import classNames from 'classnames';
import { dineroSum, safeRound } from '../../../util/util';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import Dinero from 'dinero.js';
import { TenantContext } from '../../../context/tenant-context';
import {
  CalculateOverageChargeResult,
  ProductInventoryUnit,
  ProductTrackingType,
} from '../../../clients/quipli-service/product/product';
import { useInvoiceApiV1 } from '../../../clients/quipli-service/invoice/invoice-api-v1';
import { SelectForm } from '../../../components/select-form/select-form';
import { EditableField, Field, FieldCounter } from '../../../components/field/field';
import { FinancialSummary, LineItem, SaleItemRequest } from '../../../clients/quipli-service/financials/financials';
import { orderToCalculateFinancialSummaryRequest, toUpdateOrderRequest } from './shared/utils/off-rent-utils';
import { Typography } from '../../../components/typography/typography';
import { PartialOffRentQuantities } from './off-rent-v2/partial-offrent-step';
import { useProductPricing } from './hooks/use-product-pricing';
import { useFuelChargeManagement } from './shared/hooks/use-fuel-charge-management';
import { FuelCharge } from './shared/types/fuel-charge-types';
import { InventoryReturnFuelChargeInput } from './shared/components/inventory-return-fuel-charge-input';
import { HourOverageOverride, useHourOverageCalculation } from './shared/hooks/use-hour-overage-calculation';

export const InventoryReturn = ({
  orderId,
  onCancel,
  onComplete,
}: {
  orderId: number;
  onCancel: () => void;
  onComplete: () => void;
}) => {
  const { configuration } = useContext(TenantContext);
  const { getMultipleConfigurations, listLocations, calculateFinancialSummary } = useAPI();
  const { inventoryReturn, getOrderById, updateOrder } = useOrderApiV1();
  const { createLineItem } = useInvoiceApiV1();
  const [isReturningInventory, setIsReturningInventory] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>();

  const { rentalProductPricesAsList, isLoading } = useProductPricing(order);

  const [invoice, setInvoice] = useState<Invoice | undefined>();
  const [request, setRequest] = useState<InventoryReturnRequest>();

  const [customFinancialSummary, setCustomFinancialSummary] = useState<FinancialSummary>();

  const currency = (configuration?.currency || 'USD') as Dinero.Currency;
  const [unitTrackingType, setUnitTrackingType] = useState<'serialNumber' | 'unitNumber'>('unitNumber');
  const [locations, setLocations] = useState<Location[]>();
  const [fuelUnit, setFuelUnit] = useState<string>('Gallons');
  const [isFinancialSummaryLoading, setIsFinancialSummaryLoading] = useState<boolean>(false);

  const { fuelCharges, hasAnyFuelCharge, setFuelCharges, updateFuelCharge, loading } = useFuelChargeManagement(
    order,
    true,
  );

  const { canInventoryReturn, hourOverages, hourOverageOverrides, setHourOverageOverrides, isCalculating } =
    useHourOverageCalculation(request, order, true);

  useEffect(() => {
    getOrderById(orderId)
      .then(setOrder)
      .catch((e) => toast(e.message, 'error'));

    getMultipleConfigurations(['CONF_UNIT_TRACKING_VIA', 'CONF_FUEL_UNIT']).then((configs) => {
      const unitTrackingField =
        configs.find((c) => (c.variable = 'CONF_UNIT_TRACKING_VIA'))?.value === '1' ? 'serialNumber' : 'unitNumber';
      setUnitTrackingType(unitTrackingField);
      setFuelUnit(configs.find((c) => (c.variable = 'CONF_FUEL_UNIT'))?.value || 'Gallons');
    });
    listLocations()
      .then(setLocations)
      .catch((e) => {
        console.log(e);
        toast('Error loading locations', 'error');
      });
  }, []);

  useEffect(() => {
    const fuelChangeProcessing = async () => {
      try {
        if (!order || isLoading) {
          return;
        }
        setIsFinancialSummaryLoading(true);

        const fuelChargeProducts: SaleItemRequest[] = fuelCharges
          .filter((fc) => fc.amount > 0)
          .map((fc) => ({
            id: uuidv4(),
            productId: fc.fuelChargeProductId!,
            quantity: fc.amount,
            type: OrderType.Sale,
          }));

        const request = orderToCalculateFinancialSummaryRequest(order!, rentalProductPricesAsList);

        const modifiedRequest = {
          ...request,
          items: [...request.items, ...fuelChargeProducts],
        };

        const financialSummary = await calculateFinancialSummary(modifiedRequest);

        const customPriceProducts = order.products.filter((op) =>
          financialSummary.lineItems.some(
            (li) => li.type === 'Product' && li.id === `${op.id}` && li.unitPrice.toUnit() !== op.customerBuyingPrice,
          ),
        );

        // This logic is used in the off-rent-v2 code, but it needs to have the same logic here if we are to successfully maintain custom pricing.
        // This badly needs to be consolidated.
        if (customPriceProducts.length > 0) {
          const requestWithCustomPrices = {
            ...modifiedRequest,
            items: modifiedRequest.items.map((i) => {
              const op = customPriceProducts.find((op) => i.id === `${op.id}`);
              if (op) {
                return { ...i, customPrice: safeRound(op.customerBuyingPrice * 100) };
              } else {
                return i;
              }
            }),
          };
          const newSummary = await calculateFinancialSummary(requestWithCustomPrices);
          setCustomFinancialSummary(newSummary);
        } else {
          setCustomFinancialSummary(financialSummary);
        }
      } catch (e) {
        console.log(e);
        toast('Error calculating financial summary', 'error');
      } finally {
        setIsFinancialSummaryLoading(false);
      }
    };

    fuelChangeProcessing();
  }, [fuelCharges, order, isLoading, rentalProductPricesAsList]);

  useEffect(() => {
    if (order) {
      const orderProducts = order.products.filter(
        (op) => op.type === OrderType.Rental && op.status === OrderReturnStatus.OffRent,
      );
      const request = {
        orderProductIds: orderProducts.map((op) => op.id),
        returnToLocation: order.locationId,
        hourReadings: orderProducts
          .filter((op) => op.product.allowHourTracking)
          .flatMap((op) => op.assignedUnits)
          .map((au) => {
            return { unitId: au.id, hourReading: au.lastHourReading?.hour || 0 };
          }),
        fuelCharges: [],
        chargeSecurityDeposit: false,
      };

      const newFuelCharges: FuelCharge[] = [];
      order.products.forEach((op) => {
        op?.assignedUnits.forEach((au) => {
          newFuelCharges.push({
            orderProductId: op.id,
            fuelChargeProductId: op.product.fuelChargeProductId,
            unitId: au.id,
            amount: 0,
            uuid: uuidv4() + 'fuel',
          });
        });
      });

      setFuelCharges(newFuelCharges);

      setRequest(request);
      setInvoice(order.invoices.find((inv) => inv));
    }
  }, [order]);

  const doInventoryReturn = useCallback(
    debounce(async () => {
      if (request && order && !isReturningInventory) {
        try {
          setIsReturningInventory(true);
          if (invoice) {
            if (hasAnyFuelCharge && customFinancialSummary) {
              const newOrderData = toUpdateOrderRequest(
                order,
                rentalProductPricesAsList,
                null,
                customFinancialSummary,
                currency,
              );
              const newOrder = await updateOrder(order?.id, newOrderData);
              setOrder(newOrder);
            }

            await Promise.all(
              hourOverageOverrides
                ?.filter((ov) => ov.overageCharge > 0 && request.orderProductIds.includes(ov.orderProductId))
                .map((ov) => {
                  const hourOverage = hourOverages!.find(
                    (ho) => ho.orderProductId === ov.orderProductId && ho.unitId === ov.unitId,
                  )!;
                  const op = order.products.find((op) => op.id === ov.orderProductId)!;
                  const unit = op.assignedUnits.find((au) => ov.unitId === au.id)!;
                  const unitPrice = safeRound(ov.overageCharge / hourOverage.overageHours / 100, 2);
                  return createLineItem(invoice.id, {
                    name: `Hour overage ${op.product.name} - ${unit[unitTrackingType]}`,
                    description: `${hourOverage.overageHours} hours @ ${unitPrice.toFixed(2)}/hr`,
                    unitPrice,
                    quantity: hourOverage.overageHours,
                    taxable: op.product.taxable,
                  });
                }),
            );
          }

          await inventoryReturn(orderId, request);
          await onComplete();
        } catch (err: any) {
          toast(err.message, 'error');
        } finally {
          setIsReturningInventory(false);
        }
      }
    }, 500),
    [
      request,
      order,
      invoice,
      hasAnyFuelCharge,
      customFinancialSummary,
      rentalProductPricesAsList,
      currency,
      hourOverageOverrides,
      hourOverages,
      unitTrackingType,
      isReturningInventory,
      orderId,
    ],
  );

  const { subtotal, tax, total, isValid, orderValidationMessages } = inventoryReturnCalculations({
    financialSummary: customFinancialSummary!,
    order,
    currency,
    hourOverageOverrides,
    invoice,
    inventoryReturnRequest: request,
    canInventoryReturn,
  });

  return (
    <Modal
      isOpen={true}
      title="Return Inventory"
      actions={
        !order || isReturningInventory ? null : (
          <>
            <Button className="button button-secondary" onClick={() => onCancel()}>
              Cancel
            </Button>
            <Button
              className="button button-yellow"
              disabled={!isValid || isFinancialSummaryLoading || loading}
              onClick={() => doInventoryReturn()}
            >
              {isFinancialSummaryLoading || loading ? (
                <div className="flex items-center gap-2">
                  <Spinner />
                  Calculating...
                </div>
              ) : (
                'Return'
              )}
            </Button>
          </>
        )
      }
      close={() => onCancel()}
      className="return-inventory-modal"
    >
      {!(order && request) || isReturningInventory ? (
        <Spinner />
      ) : (
        <div className="flex flex-column w-full gap-2">
          {locations && order && (
            <InventoryReturnLocation
              locations={locations}
              order={order}
              returnToLocation={locations.find((l) => request.returnToLocation === l.id)!}
              setReturnToLocation={(l) =>
                setRequest((prev) => {
                  return { ...prev!, returnToLocation: l.id };
                })
              }
            />
          )}
          {order.payments.find(
            (op) => op.reason === PaymentReason.SecurityDeposit && op.status === PaymentStatus.pending,
          ) && (
            <div className="flex gap-2 text-[0.9rem] font-bold items-center">
              <Toggle
                onClick={() => {
                  setRequest((prev) => prev && { ...prev, chargeSecurityDeposit: !prev.chargeSecurityDeposit });
                }}
                checked={request.chargeSecurityDeposit}
              />
              Charge Security Deposit
            </div>
          )}
          <div>
            <div className="grid grid-cols-4 gap-4 text-[0.9rem]">
              <div>Products</div>
              <div>
                {order.products.some((op) => op.product.trackingType === ProductTrackingType.SerialUnits) && (
                  <label>Unit</label>
                )}
              </div>
              <div></div>
              <div></div>
              {order.products
                .filter((op) => op.type === OrderType.Rental && op.status === OrderReturnStatus.OffRent)
                .flatMap((op) => {
                  const isSelected = request.orderProductIds.indexOf(op.id) >= 0;
                  const onToggle = isSelected
                    ? () => {
                        setRequest((r) => {
                          return !r
                            ? undefined
                            : { ...r, orderProductIds: r.orderProductIds.filter((opId) => opId !== op.id) };
                        });
                        setFuelCharges((r) => {
                          const existing = r?.filter((fc) => fc.orderProductId === op.id);

                          return [
                            ...(r || []).filter((fc) => fc.orderProductId !== op.id),
                            ...(existing?.map((fc) => ({
                              ...fc,
                              amount: 0,
                            })) || []),
                          ];
                        });
                      }
                    : () =>
                        setRequest((r) => {
                          return !r
                            ? undefined
                            : {
                                ...r,
                                orderProductIds: [...r.orderProductIds.filter((opId) => opId !== op.id), op.id],
                              };
                        });
                  return [
                    <div key={`${op.id}-toggle`} className="flex flex-row items-center gap-2">
                      <div>
                        <Toggle checked={isSelected} onClick={onToggle} />
                      </div>
                      <div>{op.product.name}</div>
                    </div>,
                    ...(isSelected
                      ? op.assignedUnits.flatMap((au, i) => {
                          return [
                            i !== 0 && <div key={`${au.id}-space`} />,
                            <InventoryReturnHourInput
                              readingsDisabled={false}
                              key={au.id}
                              showLabel={true}
                              unitTrackingType={unitTrackingType}
                              unit={au}
                              orderProduct={op}
                              hourReading={request.hourReadings.find((hr) => hr.unitId === au.id)?.hourReading}
                              setHourReading={(hr) =>
                                setRequest((r) => {
                                  return r
                                    ? {
                                        ...r,
                                        hourReadings: [
                                          ...r.hourReadings.filter((hr) => hr.unitId !== au.id),
                                          { unitId: au.id, hourReading: hr },
                                        ],
                                      }
                                    : undefined;
                                })
                              }
                              overage={hourOverages?.find(
                                (overage) => overage.orderProductId === op.id && overage.unitId === au.id,
                              )}
                              overageOverride={hourOverageOverrides?.find(
                                (overage) => overage.orderProductId === op.id && overage.unitId === au.id,
                              )}
                              setOverageOverride={(value) => {
                                setHourOverageOverrides((existing) => {
                                  return [
                                    ...(existing || []).filter(
                                      (ov) => ov.orderProductId !== op.id && ov.unitId !== au.id,
                                    ),
                                    value,
                                  ];
                                });
                              }}
                              isCalculating={isCalculating}
                            />,
                            <div />,
                            <div />,
                            <div>
                              {op.product.fuelChargeProductId && (
                                <>
                                  <div className="mb-2">Fuel Amount</div>
                                  <InventoryReturnFuelChargeInput
                                    orderProduct={op}
                                    fuelAmount={fuelCharges.find((fc) => fc.unitId === au.id)?.amount}
                                    setFuelAmount={(fuelAmount) =>
                                      updateFuelCharge(op.id, au.id, {
                                        amount: fuelAmount,
                                        productId: op.product.fuelChargeProductId,
                                      })
                                    }
                                    fuelUnit={fuelUnit}
                                  />
                                </>
                              )}
                            </div>,
                            <div />,
                          ];
                        })
                      : []),
                  ];
                })}
              {request.orderProductIds.length > 0 && !total.isZero() && (
                <>
                  <div></div>
                  <div></div>
                  <div>Subtotal</div>
                  <div>{subtotal.toFormat()}</div>
                  <div></div>
                  <div></div>
                  <div>Tax</div>
                  <div>{tax.toFormat()}</div>
                  <div></div>
                  <div></div>
                  <div>Total</div>
                  <div>{total.toFormat()}</div>
                </>
              )}
            </div>
          </div>
          {invoice && invoice.balance + total.toUnit() > 0 ? <></> : <div>Order has been fully paid</div>}
          <ValidationError
            className={classNames({ hidden: orderValidationMessages.length === 0 })}
            style={{ maxWidth: '342px' }}
          >
            <ul>
              {orderValidationMessages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </ValidationError>
        </div>
      )}
    </Modal>
  );
};

export const InventoryReturnLocation = ({
  locations,
  order,
  returnToLocation,
  setReturnToLocation,
  disabled = false,
}: {
  locations: Location[];
  order: Order;
  returnToLocation: Location;
  setReturnToLocation: (l: Location) => void;
  disabled?: boolean;
}) => {
  const availableLocations =
    locations?.filter((l) => l.isActive && order?.products.flatMap((op) => op.product.locationIds).includes(l.id)) ||
    [];
  return (
    <div>
      {availableLocations.length > 1 && (
        <div>
          <h6 className="m-0">Return to Location</h6>
          <SelectForm
            value={availableLocations.find((l) => l.id === returnToLocation.id)}
            options={availableLocations.filter((l) => l.isActive)}
            getOptionLabel={(l) => l.name}
            getOptionValue={(l) => l.id.toString()}
            onChange={(l) => l && setReturnToLocation(l)}
            isDisabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export const InventoryReturnHourInput = ({
  unitTrackingType,
  unit,
  orderProduct,
  hourReading,
  setHourReading,
  overage,
  overageOverride,
  setOverageOverride,
  showLabel = false,
  showUnit = true,
  labelPosition = 'top',
  readingsDisabled,
  isCalculating = false,
}: {
  unitTrackingType: 'serialNumber' | 'unitNumber';
  unit: ProductInventoryUnit;
  orderProduct: OrderProduct;
  hourReading?: number;
  setHourReading: (hr: number) => void;
  overage?: CalculateOverageChargeResult;
  overageOverride?: HourOverageOverride;
  setOverageOverride: (ov: HourOverageOverride) => void;
  showLabel?: boolean;
  showUnit?: boolean;
  labelPosition?: 'top' | 'side';
  readingsDisabled: boolean;
  isCalculating?: boolean;
}) => {
  const { configuration } = useContext(TenantContext);
  const currency = (configuration?.currency || 'USD') as Dinero.Currency;

  return (
    <>
      {showUnit && <div className="flex items-center">{unit[unitTrackingType]}</div>}
      <div className={`col-span-${showUnit ? '1' : '3'}`}>
        <div className={labelPosition === 'top' ? '' : `flex items-center`}>
          {showLabel && (
            <Typography
              type="text-xs"
              className={`mb-0 text-gray-700 ${labelPosition && labelPosition === 'top' ? '' : 'flex-1'}`}
            >
              Hours In
            </Typography>
          )}
          {orderProduct.product.allowHourTracking &&
            (readingsDisabled ? (
              <div>{hourReading} machine hours</div>
            ) : (
              <Field
                className={`${labelPosition && labelPosition === 'top' ? 'w-full' : 'flex-1'}`}
                type="number"
                value={hourReading}
                onChange={(v) => setHourReading(parseFloat(v))}
              />
            ))}
        </div>
      </div>
      <div>
        {overage &&
          overage.orderProductId === orderProduct.id &&
          overage.unitId === unit.id &&
          overage.dailyHoursLimit > 0 &&
          overage.hourlyOverageRate > 0 && (
            <>
              {readingsDisabled && (
                <>
                  <EditableField
                    type="number"
                    value={Dinero({ amount: overageOverride?.overageCharge || 0, currency }).toUnit()}
                    formattedValue={Dinero({
                      amount: overageOverride?.overageCharge || 0,
                      currency,
                    }).toFormat()}
                    resetPosition="right"
                    onChange={(value) =>
                      overageOverride &&
                      typeof value === 'number' &&
                      setOverageOverride({
                        ...overageOverride,
                        overageCharge: safeRound((value || 0) * 100),
                      })
                    }
                  />
                </>
              )}
              <Typography type="text-xs" className="text-gray-600">
                {isCalculating ? <Spinner /> : `${overage?.overageHours || 0} hours over`}
              </Typography>
            </>
          )}
      </div>
    </>
  );
};

export const QuantityOffRentInput = ({
  orderProduct,
  setOffRentRequest,
  offRentRequest,
  setPartialOffRentQuantities,
}: {
  orderProduct: OrderProduct;
  setOffRentRequest: React.Dispatch<React.SetStateAction<OffRentRequest | undefined>>;
  offRentRequest?: OffRentRequest;
  setPartialOffRentQuantities: React.Dispatch<React.SetStateAction<Dictionary<PartialOffRentQuantities>>>;
}) => {
  const offRentForThisProductId = (offRentRequest?.offRentProducts || []).find(
    (opq) => opq.productId === orderProduct.id,
  );

  const isValid =
    offRentForThisProductId && offRentForThisProductId.quantity !== undefined
      ? offRentForThisProductId.quantity <= orderProduct.quantity
      : true;

  return (
    <div className="flex flex-col gap-4 ml-8 col-span-4">
      <div>Units</div>
      <FieldCounter
        value={offRentForThisProductId ? offRentForThisProductId.quantity : orderProduct.quantity}
        min={1}
        className={classNames({ 'border-red-500': !isValid })}
        onChange={(v) => {
          setOffRentRequest((prev) => {
            if (!prev) return prev;
            const oldRequest = prev.offRentProducts.find((opq) => opq.productId === orderProduct.id);
            const restOfRequests = prev.offRentProducts.filter((opq) => opq.productId !== orderProduct.id) || [];

            if (!oldRequest) {
              return prev;
            }

            return {
              ...prev,
              offRentProducts: [...restOfRequests, { ...oldRequest, quantity: Math.floor(v) }],
            };
          });
          setPartialOffRentQuantities((prev) => {
            return {
              ...prev,
              [orderProduct.id]: { quantity: Math.floor(v), uniqueId: prev[orderProduct.id].uniqueId },
            };
          });
        }}
      />
      {!isValid && (
        <Typography type="text-xs" className="text-red-500">
          Cannot off-rent more units than were rented
        </Typography>
      )}
    </div>
  );
};

export interface InventoryReturnCalculationsResult {
  novelItems: LineItem[];
  fuelSubtotal: Dinero.Dinero;
  fuelTax: Dinero.Dinero;
  subtotal: Dinero.Dinero;
  tax: Dinero.Dinero;
  total: Dinero.Dinero;
  balance: number;
  isValid: boolean | undefined;
  orderValidationMessages: string[];
}

export function inventoryReturnCalculations({
  financialSummary,
  order,
  currency,
  hourOverageOverrides,
  invoice,
  inventoryReturnRequest,
  canInventoryReturn,
  lateChargeTotal,
}: {
  financialSummary?: FinancialSummary;
  order?: Order;
  currency: Dinero.Currency;
  hourOverageOverrides: HourOverageOverride[];
  invoice?: Invoice;
  inventoryReturnRequest?: InventoryReturnRequest;
  canInventoryReturn?: CanInventoryReturnValidationError[];
  lateChargeTotal?: Dinero.Dinero;
}): InventoryReturnCalculationsResult {
  const novelItems =
    financialSummary?.lineItems.filter(
      (li) => li.type === 'Product' && !order?.products.find((op) => op.id.toString() === li.id),
    ) || [];

  const fuelSubtotal = dineroSum(
    novelItems.map((li) => Dinero({ amount: li.amount.getAmount(), currency })),
    currency,
  );
  const fuelTax = dineroSum(
    novelItems
      .filter((li) => li.taxable)
      .map((li) => Dinero({ amount: li.amount.getAmount(), currency }))
      .map((amount) => amount.multiply(order?.taxPercentage || 0)),
    currency,
  );

  const subtotal = dineroSum(
    hourOverageOverrides
      .filter((ov) => inventoryReturnRequest?.orderProductIds.includes(ov.orderProductId))
      .map((ov) => Dinero({ amount: ov.overageCharge, currency })),
    currency,
  )
    .add(fuelSubtotal)
    .add(lateChargeTotal || Dinero({ amount: 0, currency }));

  const tax = dineroSum(
    hourOverageOverrides
      .filter((ov) => inventoryReturnRequest?.orderProductIds.includes(ov.orderProductId) && ov.taxable)
      .map((ov) => Dinero({ amount: ov.overageCharge, currency })),
    currency,
  )
    .multiply(order?.taxPercentage || 0)
    .add(fuelTax);
  const total = subtotal.add(tax);
  const balance = (invoice?.balance || 0) + total.toUnit();
  const isValid =
    inventoryReturnRequest &&
    inventoryReturnRequest.orderProductIds.length > 0 &&
    canInventoryReturn &&
    canInventoryReturn.length === 0;

  const orderValidationMessages = [...(canInventoryReturn ? canInventoryReturn.map((_) => _.message) : [])];

  return {
    novelItems,
    fuelSubtotal,
    fuelTax,
    subtotal,
    tax,
    total,
    balance,
    isValid,
    orderValidationMessages,
  };
}
